import { addDays, startOfDay } from "date-fns";
import { LocalCalendarBlock, LocalShift, LocalVisitResource } from "~/hooks/visitDragDropReducer";
import { OrgConfig } from "~/providers/OrgConfigProvider";
import { visitResourceStatus } from "~/utils/statuses";
import { RouteLocalCalendarBlock, RouteLocalVisitResource, RouteProviderContext, RouteShiftContext } from "../route/types";

type BuildRouteLocalDataArgs = {
  providerId: number;
  date: Date;
  localVisitResources: LocalVisitResource[];
  localCalendarBlocks: LocalCalendarBlock[];
  localShifts: LocalShift[];
  providerAddressCoordinates: string | null;
  orgConfig: OrgConfig;
};

export type RouteLocalData = {
  localVisitResources: RouteLocalVisitResource[];
  localCalendarBlocks: RouteLocalCalendarBlock[];
  providerContext: RouteProviderContext;
  shiftContext?: RouteShiftContext;
};

export function buildRouteLocalData({
  providerId,
  date,
  localVisitResources,
  localCalendarBlocks,
  localShifts,
  providerAddressCoordinates,
  orgConfig,
}: BuildRouteLocalDataArgs): RouteLocalData {
  const routeFilterStart = startOfDay(date);
  const routeFilterEnd = addDays(routeFilterStart, 1);
  const start = routeFilterStart.getTime();
  const end = routeFilterEnd.getTime();

  const mappedVisitResources: RouteLocalVisitResource[] = localVisitResources
    .filter((vr) => {
      if (vr.providerId !== providerId || visitResourceStatus(vr) === "canceled" || !vr.visit?.patient) {
        return false;
      }
      const t = new Date(vr.startTime).getTime();
      return t >= start && t < end;
    })
    .map((vr) => ({
      __typename: "VisitResourceSelectItem",
      startTime: vr.startTime,
      addressCoordinates: vr.addressCoordinates,
      resourceRequirement: {
        inHome: orgConfig.visitResourceRequirements[vr.visitResourceRequirementId!]?.inHome ?? true,
      },
      visit: vr.visit
        ? {
            addressCoordinates: vr.visit.addressCoordinates ?? null,
            patient: vr.visit.patient
              ? {
                  id: vr.visit.patient.id,
                  firstName: vr.visit.patient.firstName ?? "",
                  lastName: vr.visit.patient.lastName ?? "",
                }
              : null,
          }
        : null,
    }));

  const mappedCalendarBlocks: RouteLocalCalendarBlock[] = localCalendarBlocks
    .filter((cb) => {
      if (cb.providerId !== providerId || cb.eventStatus === "canceled" || !cb.addressCoordinates) {
        return false;
      }
      const t = new Date(cb.startTime).getTime();
      return t >= start && t < end;
    })
    .map((cb) => ({
      __typename: "CalendarBlockSelectItemWithFilters",
      startTime: cb.startTime,
      addressCoordinates: cb.addressCoordinates ?? null,
      title: cb.title ?? null,
    }));

  const shift = localShifts.find((s) => {
    if (s.provider?.id !== providerId || s.status !== "confirmed") {
      return false;
    }
    const shiftStart = new Date(s.startTime).getTime();
    return shiftStart >= start && shiftStart < end;
  });

  return {
    localVisitResources: mappedVisitResources,
    localCalendarBlocks: mappedCalendarBlocks,
    providerContext: {
      addressCoordinates: providerAddressCoordinates,
    },
    shiftContext: shift
      ? {
          startTime: new Date(shift.startTime),
          endTime: new Date(shift.endTime),
          addressCoordinates: shift.addressCoordinates,
          providerId: shift.providerId,
          useShiftCenterDriveTimes: shift.useShiftCenterDriveTimes,
          shiftCenter: shift.shiftCenterId
            ? {
                addressCoordinates: orgConfig.shiftCenters[shift.shiftCenterId]?.addressCoordinates ?? null,
              }
            : null,
        }
      : undefined,
  };
}
